
import ApiError from 'components/common/ApiError';
import LoaderComponent from 'components/common/Loader';
import { API_ENDPOINTS } from 'constants';
import { useFetchData } from 'hooks/apiHooks';
import React, { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BackgroundColorContext } from "contexts/BackgroundColorContext";
import { backgroundColors } from "contexts/BackgroundColorContext";
import { ability } from "abilities/AbilityProvider";
import { defineUserAbilities } from "abilities/abilities";
import { OBJ } from 'constants';
import { useList } from 'hooks/useList';

const ACCESS_LEVEL = {
    null: 'Administrator',
    'Administrator': 'AdministratorSpeicherfuchs'
}

export const UserSettingsContext = createContext({ tenant: null, refetchTenant: null, userSettings: null, isLoading: true,  error: null,});

export const UserSettingsProvider = ({ children }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const invitationData = sessionStorage.getItem("invitationToken");
;
    const { data: tenant, saveKey: saveTenantKey, isLoading:isLoadingTenant, error: errorTenant, refetch: refetchTenant} = useFetchData(API_ENDPOINTS.TENANTS.CURRENT, null, false);
    const { data, isLoading, error, saveLineKey, setAndSave, refetch, success, saveError } = useFetchData(API_ENDPOINTS.SETTINGS.USER_SETTINGS, null, false);
    const { data: apps, isLoading: isLoadingApps, error: errorApps, refetch: refetchApps } = useFetchData(API_ENDPOINTS.APPS.LIST, null, false);//useList(OBJ.APPS, null, null, null, JSON.stringify({}));
    const { data: userId, isLoading: isLoadingUserId, error: errorUserId, refetch: refetchUserId } = useFetchData(API_ENDPOINTS.USERS.ID, null, false);
    const invitation = useFetchData(API_ENDPOINTS.USERS.ACCEPT_INVITATION, null, false);
    const { color, changeColor } = useContext(BackgroundColorContext);

    const currentUser = () => {
        return {userRole: userId.user_role, tenantRole: ACCESS_LEVEL[userId.tenant_role], };
    };

    const settingKeyValues = useCallback((settings) => {
        if (!Array.isArray(settings)) return {};
        return settings.filter(item => item.id > 0).reduce((acc, item) => {
            let value = item.value
            if(item.value === 'false')value=false
            if(item.value === 'true')value=true
            acc[item.key] = value;
            return acc;
        }, {});
    }, []);
    
    useEffect(() => {
        if (userId?.user_id && userId.tenant_role === null) {
            changeColor(backgroundColors.blue);
        }
        if (userId?.tenant_role && backgroundColors[userId.tenant_role] && color !== backgroundColors[userId.tenant_role] ) {
            changeColor(backgroundColors[userId.tenant_role]);
        }
    }, [userId?.tenant_role]); 

    useEffect(() => {
        if (userId?.user_id && currentUser()) {
            ability.update((defineUserAbilities(currentUser()) || { rules: [] }).rules);
        }
    }, [currentUser]);  

    const userSettings = useMemo(() => settingKeyValues(data), [data, settingKeyValues]);

    useEffect(() => {
        if (invitationData) {
            const { token, tenant_id } = JSON.parse(invitationData);
            invitation.refetch({token: token, tenant_id: tenant_id}).then(()=>refetchTenant()).then(sessionStorage.clear())
        }
    }, [invitationData]);

    const refreshTenantData = async () => {
        const oldTenantId = userId?.tenant_id
        const newUserId = await refetchUserId()

        if (newUserId?.tenant_id && newUserId.tenant_id != oldTenantId){
            refetchTenant(null, newUserId.tenant_id)
            refetch(null, newUserId.tenant_id)
            console.log('user settings context', newUserId.tenant_id)
            refetchApps(null, newUserId.tenant_id)
        }
        if (newUserId?.tenant_id && apps?.length > 0 && !apps.find(app => app.name === userSettings.selected_app)) {
            updateSetting('selected_app', apps[0].name);
        }
    }

    useEffect(() => {
        console.log('check user id', userId, tenant)
        if(!userId || !userId.tenant_id){
            refreshTenantData()
        }
    }, [userId]);

    useEffect(() => {
        if (data && !isLoading) {
            initLanguage(settingKeyValues(data));
        }
    }, [data, isLoading, settingKeyValues]);

    const updateSetting = (key, newValue) => {
        /*todo: Correct!*/
        if(!data){
            refetch()
            return
        }
        console.log(data)
        const id = data.find(item => item.key === key)?.id;
        saveLineKey(id, 'value', newValue);
        console.log(`Updated settings: ${key} = ${newValue}`);
    };
    
    const initLanguage = useCallback(async (settings) => {
        try {
            const languageCode = settings.language;
            await i18n.changeLanguage(languageCode);
        } catch (err) {
            console.error('Error initializing user settings:', err);
        }
    }, [i18n]);

    const contextValue = useMemo(() => ({
        tenant, saveTenantKey, isLoadingTenant, errorTenant, refetchTenant,
        apps, isLoadingApps, errorApps, refetchApps,
        data, isLoading, error, saveLineKey, refetch, success, saveError,
        userSettings, updateSetting, invitation, invitationData, userId, currentUser, refreshTenantData
    }), [ tenant, saveTenantKey, isLoadingTenant, errorTenant, refetchTenant,
        apps, isLoadingApps, errorApps, refetchApps,
        data, isLoading, error, saveLineKey, refetch, success, saveError,
        userSettings, updateSetting, invitation, invitationData, userId, currentUser, refreshTenantData]);

    return (
        <UserSettingsContext.Provider value={contextValue}>
            {(isLoading || isLoadingTenant || isLoadingApps || isLoadingUserId) ? (
                <LoaderComponent/>
            ) : error ? (
                <ApiError retryFetch={refetch} errorMessage={error.message} />
            ) : (
                children
            )}
        </UserSettingsContext.Provider>
    );
};